// HTTP transport for activity events. Two flush modes:
//   - flush()       — regular fetch with ~standard semantics
//   - flushBeacon() — fetch with `keepalive: true`, used in beforeunload
//                     so the request survives page navigation. sendBeacon
//                     can't set Authorization headers, hence keepalive.

import type { ActivityEvent } from './useActivityTracker.types'
import { getSupabaseSession } from './useActivityTracker.helpers'

export interface ActivityFlusher {
  flush: (events: ActivityEvent[]) => void
  flushBeacon: (events: ActivityEvent[]) => void
}

export function createFlusher(): ActivityFlusher {
  const supabaseUrl = ((import.meta.env.VITE_SUPABASE_URL || '') as string).replace(/\/$/, '')
  const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || '') as string

  // No-op transport when Supabase isn't configured (e.g. local dev without
  // env vars). The hook still works — events accumulate but are dropped.
  if (!supabaseUrl || !anonKey) {
    return {
      flush: () => { /* no-op: Supabase not configured */ },
      flushBeacon: () => { /* no-op: Supabase not configured */ },
    }
  }

  const endpoint = `${supabaseUrl}/rest/v1/scout_activity_log`

  function buildHeaders(accessToken: string): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'apikey': anonKey,
      'Authorization': `Bearer ${accessToken}`,
      'Prefer': 'return=minimal',
    }
  }

  function flush(events: ActivityEvent[]): void {
    if (events.length === 0) return
    const session = getSupabaseSession()
    if (!session) return // Not authenticated — drop silently (logged out)

    fetch(endpoint, {
      method: 'POST',
      headers: buildHeaders(session.accessToken),
      body: JSON.stringify(events),
    }).catch((err) => {
      // Telemetry is fire-and-forget — never break the app — but we DO
      // surface the failure to console so a broken endpoint doesn't go
      // unnoticed during local dev or staging (D-33: no silent failure).
      console.warn('[activity-tracker] flush failed', err)
    })
  }

  function flushBeacon(events: ActivityEvent[]): void {
    if (events.length === 0) return
    const session = getSupabaseSession()
    if (!session) return

    try {
      fetch(endpoint, {
        method: 'POST',
        headers: buildHeaders(session.accessToken),
        body: JSON.stringify(events),
        keepalive: true,
      }).catch((err) => {
        console.warn('[activity-tracker] beacon flush failed', err)
      })
    } catch (err) {
      // keepalive fetch may throw synchronously if payload > 64KB.
      // Drop the batch — no point queuing it for later, the page is
      // about to unload — but log so we know about oversized payloads.
      console.warn('[activity-tracker] beacon flush threw (payload too large?)', err)
    }
  }

  return { flush, flushBeacon }
}
